import React, { useState, useRef, useEffect } from 'react';
import { Bell, X, Check, CheckCheck, Clock, Wallet, ShoppingCart, Gift, User, TrendingUp, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'balance_credited':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'balance_debited':
        return <Wallet className="w-4 h-4 text-red-500" />;
      case 'code_purchased':
        return <ShoppingCart className="w-4 h-4 text-blue-500" />;
      case 'topup_order_created':
        return <Zap className="w-4 h-4 text-blue-500" />;
      case 'topup_order_message':
        return <Bell className="w-4 h-4 text-amber-500" />;
      case 'recharge_approved':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'recharge_rejected':
        return <X className="w-4 h-4 text-red-500" />;
      case 'transaction_completed':
        return <CheckCheck className="w-4 h-4 text-green-500" />;
      case 'promotion':
        return <Gift className="w-4 h-4 text-purple-500" />;
      case 'account_update':
        return <User className="w-4 h-4 text-blue-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Il y a ${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `Il y a ${diffInDays}j`;
  };

  const resolveNotificationTarget = (notification) => {
    const orderId = String(
      notification?.data?.orderId ||
      notification?.data?.topupOrderId ||
      ''
    ).trim();

    if (userProfile?.role === 'reseller') {
      if (
        orderId &&
        (
          notification.type === 'balance_debited' ||
          notification.type === 'balance_credited' ||
          notification.type === 'topup_order_message'
        )
      ) {
        return `/operations?topupOrderId=${encodeURIComponent(orderId)}`;
      }

      if (notification.type === 'balance_debited' || notification.type === 'balance_credited') {
        return '/operations';
      }
    }

    if (
      (userProfile?.role === 'admin' || userProfile?.role === 'operator') &&
      (notification.type === 'topup_order_created' || notification.type === 'topup_order_message')
    ) {
      const basePath = userProfile?.role === 'operator' ? '/operator/topup-orders' : '/admin/topup/orders';
      return orderId
        ? `${basePath}?orderId=${encodeURIComponent(orderId)}`
        : basePath;
    }

    return null;
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    const target = resolveNotificationTarget(notification);
    setIsOpen(false);

    if (target) {
      navigate(target);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bouton de notification */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
      >
        <Bell className="h-6 w-6" />
        {/* Badge de notification */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown des notifications */}
      {isOpen && (
        <div className="fixed top-16 right-4 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-[99999]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" />
              Notifications
            </h3>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                  title="Marquer tout comme lu"
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Tout lire
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Liste des notifications */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Aucune notification</p>
                <p className="text-xs text-gray-400 mt-1">
                  {userProfile?.role === 'reseller' 
                    ? 'Les mouvements de solde et les reponses sur vos commandes apparaissent ici'
                    : 'Les notifications d\'achat, solde et transactions apparaîtront ici'
                  }
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            !notification.read ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </p>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                          )}
                        </div>
                        <p className={`text-sm mt-1 ${
                          !notification.read ? 'text-gray-700' : 'text-gray-500'
                        }`}>
                          {notification.message}
                        </p>
                        <div className="flex items-center mt-2 text-xs text-gray-400">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatTimeAgo(notification.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <button className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium">
                Voir toutes les notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
